import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import DashboardCard from '../../../components/shared/DashboardCard';
import Card from '../../../components/shared/Card';
import Modal from '../../../components/shared/Modal';
import { getPriorityColors, buttonGradients, themeColors, getScoreGradient } from '../../../utils/colorUtils';
import { useTheme } from '../../../contexts/ThemeContext';
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";

// Helper function to format currency values
const formatCurrency = (amount) => {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Helper function to determine the primary action button based on task content
const getPrimaryAction = (taskText) => {
  const taskLower = taskText?.toLowerCase() || '';
  
  if (taskLower.includes('rebalance') || taskLower.includes('portfolio') || taskLower.includes('investment')) {
    return { text: 'Execute Trades', icon: 'exchange' };
  }
  if (taskLower.includes('meeting') || taskLower.includes('review')) {
    return { text: 'Schedule Review', icon: 'calendar' };
  }
  if (taskLower.includes('email')) {
    return { text: 'Send Email', icon: 'mail' };
  }
  if (taskLower.includes('text') || taskLower.includes('message')) {
    return { text: 'Send Text', icon: 'chat' };
  }
  if (taskLower.includes('document') || taskLower.includes('form')) {
    return { text: 'Mail Documents', icon: 'document' };
  }
  if (taskLower.includes('rmd') || taskLower.includes('required minimum')) {
    return { text: 'Calculate RMD', icon: 'calculator' };
  }
  
  // Default action
  return { text: 'Take Action', icon: 'lightning' };
};

// Helper function for priority styling
const getPriorityStyle = (tag) => {
  if (!tag) return { bg: 'bg-gray-100', text: 'text-gray-800', border: 'bg-gray-500' };
  
  if (typeof tag.color === 'string') {
    // Use the getPriorityColors function
    return getPriorityColors(tag.text);
  }
  
  // If the tag already has color classes, use those
  return {
    bg: tag.color?.split(' ')[0] || 'bg-gray-100',
    text: tag.color?.split(' ')[1] || 'text-gray-800',
    border: 'bg-gray-500'
  };
};

// Helper function to determine task type and status tags
const getTaskTags = (subtasks) => {
  const isMultiStep = subtasks.length > 1;
  const completedSubtasks = subtasks.filter(st => st.isComplete).length;
  const isNotStarted = completedSubtasks === 0;
  const tags = [];
  
  if (isMultiStep) {
    tags.push({ text: 'Multi-Step', bg: 'bg-purple-100', textColor: 'text-purple-800' });
  } else {
    tags.push({ text: 'Single-Step', bg: 'bg-indigo-100', textColor: 'text-indigo-800' });
  }
  
  if (isNotStarted) {
    tags.push({ text: 'Not Started', bg: 'bg-gray-100', textColor: 'text-gray-700' });
  }
  
  return tags;
};

// Helper function to extract a concise title from task text
const extractTitle = (text) => {
  // Map of task keywords to specific titles - with more specific matches first
  const titleMappings = {
    'rebalance portfolio': 'Rebalance Portfolio',
    'review portfolio': 'Review Portfolio Allocation',
    'portfolio allocation': 'Review Portfolio Allocation',
    'rebalance': 'Portfolio Rebalancing',
    'portfolio': 'Portfolio Assessment',
    'annual review': 'Schedule Annual Review',
    'client review': 'Schedule Client Review',
    'meeting': 'Schedule Client Meeting', 
    'review': 'Client Review',
    'rmd': 'Process RMD: Kim Household',
    'required minimum': 'Process Required Minimum Distribution',
    'document upload': 'Upload Client Documents',
    'document': 'Process Client Documents',
    'form': 'Complete Required Forms',
    'email client': 'Email Client',
    'email': 'Send Client Communication',
    'contact client': 'Contact Client',
    'contact': 'Client Communication'
  };
  
  // Check for specific keywords in text - use more specific matches first
  const textLower = text.toLowerCase();
  for (const [keyword, title] of Object.entries(titleMappings)) {
    if (textLower.includes(keyword)) {
      return title;
    }
  }
  
  // If no matching keyword, fall back to original extraction logic
  const sentenceMatch = text.match(/^[^.!?]+[.!?]/);
  if (sentenceMatch) {
    return sentenceMatch[0].trim();
  }
  
  const commaMatch = text.match(/^[^,]+,/);
  if (commaMatch) {
    return commaMatch[0].replace(',', '').trim();
  }
  
  return text.length > 40 ? `${text.substring(0, 40)}...` : text;
};

// Helper function to generate mock subtasks based on task text
const generateSubtasks = (text) => {
  const taskLower = text.toLowerCase();
  
  if (taskLower.includes('rebalance') || taskLower.includes('portfolio')) {
    return [
      "Assess current asset allocation.",
      "Execute trades to increase Risk Profile by 5 points.",
      "Verify alignment with client risk profile."
    ];
  } else if (taskLower.includes('meeting') || taskLower.includes('review')) {
    return [
      "Send meeting invitation to client.",
      "Prepare portfolio performance report.",
      "Review long-term financial goals."
    ];
  } else if (taskLower.includes('rmd') || taskLower.includes('required minimum')) {
    return [
      "Calculate minimum distribution amount.",
      "Email client with withdrawal options.",
      "Confirm compliance with IRS regulations."
    ];
  } else if (taskLower.includes('email') || taskLower.includes('contact')) {
    return [
      "Prepare client communication.",
      "Send documentation and schedule follow-up."
    ];
  } else if (taskLower.includes('document') || taskLower.includes('form')) {
    return [
      "Complete required paperwork.",
      "Collect client signatures.",
      "Submit forms for processing."
    ];
  } else {
    return [
      "Analyze client situation.",
      "Implement recommended action."
    ];
  }
};

// Task Detail Modal Component
const TaskDetailModal = ({ isOpen, onClose, task, onComplete, onAddNote, onSetReminder, onSubtaskUpdate, onOpenTradeExecution }) => {
  const [note, setNote] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  const [subtasks, setSubtasks] = useState([]);
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissError, setDismissError] = useState('');
  const priorityColors = getPriorityStyle(task?.tag);
  
  useEffect(() => {
    if (task?.text) {
      setSubtasks(generateSubtasks(task.text).map((text, index) => ({
        text,
        isComplete: false
      })));
    }
  }, [task]);
  
  const toggleSubtask = (index) => {
    const updatedSubtasks = [...subtasks];
    updatedSubtasks[index].isComplete = !updatedSubtasks[index].isComplete;
    setSubtasks(updatedSubtasks);
    
    // Update parent component
    if (onSubtaskUpdate && task?.id) {
      onSubtaskUpdate(task.id, updatedSubtasks.map(st => st.isComplete));
    }
  };
  
  const handleDismiss = () => {
    if (dismissReason.trim().length < 10) {
      setDismissError('Please provide detailed feedback (at least 10 characters)');
      return;
    }
    
    setDismissError('');
    console.log(`Task "${task?.text}" dismissed with reason: ${dismissReason}`);
    // In a real app, you would send this to the backend
    
    setShowDismissDialog(false);
    onClose();
  };
  
  const renderActionButtons = () => {
    const primaryAction = getPrimaryAction(task?.text || '');
    const isMultiStep = subtasks.length > 1;
    const allComplete = subtasks.every(st => st.isComplete);
    const anyComplete = subtasks.some(st => st.isComplete);
    
    return (
      <div className="space-y-3">
        {/* Primary actions based on task type */}
        {!task?.isChecked && (
          <>
            {/* Main action button */}
            <button 
              onClick={() => {
                // Call the appropriate function based on action type
                if (primaryAction.text === 'Execute Trades' && onOpenTradeExecution) {
                  onOpenTradeExecution();
                } else {
                  console.log(`Primary action: ${primaryAction.text}`);
                  // In a real app, you would handle other action types
                }
              }}
              className={`w-full px-4 py-2 rounded text-white font-medium transition-colors duration-200 ${buttonGradients.success}`}
            >
              {primaryAction.text}
            </button>
            
            {/* If multi-step, show step-specific action buttons */}
            {isMultiStep && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500 font-medium">Step-specific Actions:</p>
                {subtasks.map((subtask, idx) => {
                  if (!subtask.isComplete) {
                    // Generate a button specific to each incomplete step
                    const actionText = subtask.text.includes("Send") ? "Send Message" :
                                     subtask.text.includes("Prepare") ? "Generate Report" :
                                     subtask.text.includes("Execute") ? "Place Trades" :
                                     subtask.text.includes("Discuss") ? "Schedule Call" :
                                     subtask.text.includes("Calculate") ? "Run Calculation" :
                                     subtask.text.includes("Email") ? "Compose Email" :
                                     subtask.text.includes("Verify") ? "Verify Alignment" :
                                     subtask.text.includes("Confirm") ? "Confirm Details" :
                                     subtask.text.includes("Collect") ? "Request Signature" :
                                     "Complete Step";
                    
                    // For the execution step, connect it to the trade execution modal
                    const handleStepAction = () => {
                      if (actionText === "Place Trades" && onOpenTradeExecution) {
                        onOpenTradeExecution();
                      } else {
                        console.log(`Step action: ${actionText}`);
                        // In a real app, handle other step actions
                      }
                    };
                    
                    return (
                      <button
                        key={idx}
                        onClick={handleStepAction}
                        className="w-full px-4 py-1.5 rounded text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium transition-colors duration-200 flex items-center justify-center"
                      >
                        <span className="mr-1">{idx + 1}.</span> {actionText}
                      </button>
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </>
        )}
        
        {/* Complete/Incomplete task button */}
        <button 
          onClick={() => onComplete && onComplete(task?.id)}
          className={`w-full px-4 py-2 rounded text-white font-medium transition-colors duration-200 ${buttonGradients.primary}`}
        >
          {task?.isChecked ? 'Mark as Incomplete' : 
           allComplete ? 'Mark Task Complete' : 
           anyComplete ? 'Continue Progress' : 'Start Task'}
        </button>
        
        {/* Dismiss button */}
        <button 
          onClick={() => setShowDismissDialog(true)}
          className="w-full px-4 py-2 rounded bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors duration-200"
        >
          Dismiss Suggestion
        </button>
      </div>
    );
  };
  
  // Render dismiss dialog
  const renderDismissDialog = () => {
    if (!showDismissDialog) return null;
    
    return (
      <Modal
        isOpen={showDismissDialog}
        onClose={() => setShowDismissDialog(false)}
        size="sm"
      >
        <div className="p-6">
          <h5 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Dismiss this suggestion?</h5>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Please provide feedback about why you're dismissing this suggestion so we can improve future recommendations.
          </p>
          <textarea
            className={`w-full px-3 py-2 text-gray-700 dark:text-gray-200 border ${dismissError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 mb-2`}
            rows="2"
            placeholder="Reason for dismissing this suggestion..."
            value={dismissReason}
            onChange={(e) => {
              setDismissReason(e.target.value);
              if (e.target.value.trim()) setDismissError('');
            }}
          ></textarea>
          {dismissError && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{dismissError}</p>}
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setShowDismissDialog(false)}
              className="px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 text-sm transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-sm transition-colors duration-200"
            >
              Confirm Dismiss
            </button>
          </div>
        </div>
      </Modal>
    );
  };
  
  // Move getTaskImpact inside the component for use
  const getTaskImpact = (taskText) => {
    // Default values
    let riskImpact = 0;
    let healthImpact = 0;
    
    if (!taskText) return { riskImpact, healthImpact };
    
    // Set impact values based on task content
    const taskLower = taskText.toLowerCase();
    
    if (taskLower.includes('rebalance')) {
      if (taskLower.includes('reduce risk')) {
        riskImpact = -5; // Negative means reducing risk
      } else if (taskLower.includes('increase risk')) {
        riskImpact = 5;  // Positive means increasing risk
      } else {
        riskImpact = 2;  // Default rebalance impact
      }
      healthImpact = 3;
    } else if (taskLower.includes('rmd') || taskLower.includes('required minimum')) {
      riskImpact = 0;
      healthImpact = 5;
    } else if (taskLower.includes('tax loss')) {
      riskImpact = 0;
      healthImpact = 4;
    } else {
      // Default modest impacts for other tasks
      riskImpact = 1;
      healthImpact = 2;
    }
    
    return { riskImpact, healthImpact };
  };
  
  if (!isOpen) return null;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-3
              ${priorityColors.bg} ${priorityColors.text}`}>
              {task?.tag?.text || 'Task'}
            </span>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {task?._forcedTitle || (task ? extractTitle(task.text) : 'Task Details')}
            </h3>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200"
          >
            <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          {/* Left Column - Task Details */}
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Task Description</h4>
              <p className="text-gray-700 dark:text-gray-300 mb-6">{task?.text}</p>
              
              {/* Subtasks with checkboxes */}
              <div className="mt-4">
                <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Subtasks:</h5>
                <ul className="space-y-2">
                  {subtasks.map((subtask, idx) => (
                    <li key={idx} className="flex items-start">
                      <input
                        type="checkbox"
                        id={`subtask-${idx}`}
                        checked={subtask.isComplete}
                        onChange={() => toggleSubtask(idx)}
                        className="h-5 w-5 text-blue-600 rounded border-gray-300 dark:border-gray-600 mt-0.5 focus:ring-blue-500"
                      />
                      <label
                        htmlFor={`subtask-${idx}`}
                        className={`ml-2 text-gray-700 dark:text-gray-300 ${subtask.isComplete ? 'line-through text-gray-400 dark:text-gray-500' : ''}`}
                      >
                        {subtask.text}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Overall Status:</span>
                <span className={`text-sm font-medium ${task?.isChecked ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`}>
                  {task?.isChecked ? 'Completed' : 'Pending'}
                </span>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Add Notes</h4>
              <textarea
                className="w-full px-3 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="4"
                placeholder="Add your notes about this task..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              ></textarea>
              <button
                onClick={() => onAddNote && onAddNote(task?.id, note)}
                className="mt-3 px-4 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors duration-200"
              >
                Save Note
              </button>
            </div>
          </div>

          {/* Right Column - Analysis & Actions */}
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Potential Impact</h4>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700 dark:text-gray-300">Risk Profile</span>
                    <span className="font-medium text-green-600 dark:text-green-400">{getTaskImpact(task?.text).riskImpact} points</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500" style={{ width: `${Math.min(100, Math.abs(getTaskImpact(task?.text).riskImpact) * 10)}%` }}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700 dark:text-gray-300">Financial Health</span>
                    <span className="font-medium text-blue-600 dark:text-blue-400">+{getTaskImpact(task?.text).healthImpact}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500" style={{ width: `${Math.min(100, getTaskImpact(task?.text).healthImpact * 10)}%` }}></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Set Reminder</h4>
              <div className="flex items-end space-x-3">
                <div className="flex-grow">
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Reminder Date</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={reminderDate}
                    onChange={(e) => setReminderDate(e.target.value)}
                  />
                </div>
                <button
                  onClick={() => onSetReminder && onSetReminder(task?.id, reminderDate)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors duration-200"
                >
                  Set Reminder
                </button>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Actions</h4>
              {renderActionButtons()}
              {renderDismissDialog()}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Trade Execution Modal Component
const TradeExecutionModal = ({ isOpen, onClose, task, taskImpact, onExecuteTrades }) => {
  const [executionStep, setExecutionStep] = useState('review'); // review, confirm, success
  const [isExecuting, setIsExecuting] = useState(false);
  const [activeTab, setActiveTab] = useState('industry'); // industry, geography, assetClass
  const [editingTradeIndex, setEditingTradeIndex] = useState(null);
  const [editForm, setEditForm] = useState({
    shares: 0,
    price: "",
    limitPrice: "",
    orderType: "Market" // Market or Limit
  });
  const { isDarkMode } = useTheme();
  
  // Get today's date for display
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  const formattedTime = today.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
  
  // Sample data with updated values - in a real app, this would come from your API
  const portfolioData = {
    clientName: "Michael Smith",
    institution: "Charles Schwab - Contributory IRA",
    accountNumber: "1234-5678",
    value: "$423,356.21",
    performancePercent: 0.37,
    dateRange: `Updated: ${formattedTime}`,
    currentAllocation: {
      // Industry allocation (11 standard sectors)
      industry: {
        informationTechnology: 22,
        consumerDiscretionary: 18,
        financials: 14,
        healthcare: 11,
        industrials: 9,
        communication: 7,
        consumerStaples: 6,
        energy: 5,
        materials: 4,
        utilities: 3,
        realEstate: 1
      },
      // Geographic allocation
      geography: {
        northAmerica: 68,
        europe: 16,
        asiaPacific: 10,
        emergingMarkets: 5,
        japan: 1
      },
      // Asset class allocation
      assetClass: {
        domesticEquity: 60,
        internationalEquity: 20,
        fixedIncome: 12,
        alternatives: 5,
        cash: 3
      }
    },
    targetAllocation: {
      // Industry allocation (11 standard sectors)
      industry: {
        informationTechnology: 20,
        consumerDiscretionary: 15,
        financials: 16,
        healthcare: 12,
        industrials: 10,
        communication: 8,
        consumerStaples: 7,
        energy: 5,
        materials: 4,
        utilities: 2,
        realEstate: 1
      },
      // Geographic allocation
      geography: {
        northAmerica: 65,
        europe: 15,
        asiaPacific: 12,
        emergingMarkets: 7,
        japan: 1
      },
      // Asset class allocation
      assetClass: {
        domesticEquity: 58,
        internationalEquity: 22,
        fixedIncome: 12,
        alternatives: 6,
        cash: 2
      }
    },
    suggestedTrades: [
      { action: "Sell", symbol: "JPST", name: "JPMorgan Ultra-Short Income ETF", shares: 550, price: "$50.53", amount: "$27,791.50", sector: "Fixed Income" },
      { action: "Sell", symbol: "AAPL", name: "Apple Inc", shares: 25, price: "$187.35", amount: "$4,683.75", sector: "Information Technology" },
      { action: "Sell", symbol: "MSFT", name: "Microsoft Corp", shares: 12, price: "$390.27", amount: "$4,683.24", sector: "Information Technology" },
      { action: "Buy", symbol: "JPM", name: "JPMorgan Chase & Co", shares: 32, price: "$195.47", amount: "$6,255.04", sector: "Financials" },
      { action: "Buy", symbol: "XOM", name: "Exxon Mobil Corp", shares: 37, price: "$114.38", amount: "$4,232.06", sector: "Energy" },
      { action: "Buy", symbol: "CVX", name: "Chevron Corp", shares: 22, price: "$146.65", amount: "$3,226.30", sector: "Energy" },
      { action: "Buy", symbol: "AMZN", name: "Amazon.com Inc", shares: 65, price: "$178.25", amount: "$11,586.25", sector: "Consumer Discretionary" },
      { action: "Buy", symbol: "TSLA", name: "Tesla Inc", shares: 40, price: "$176.35", amount: "$7,054.00", sector: "Consumer Discretionary" }
    ],
    impactMetrics: {
      householdRiskAlignment: { value: "+6.9%", color: "text-green-600" },
      riskToReward: { value: "+0.23%", color: "text-green-600" },
      householdReturnPotential: { value: "+0.29%", color: "text-green-600" }
    }
  };

  // Render dynamic donut chart based on the current active tab
  const renderDonutChart = () => {
    // Get the allocation data based on active tab
    let segments = [];
    let allocationData = {};
    
    if (activeTab === 'industry') {
      allocationData = portfolioData.currentAllocation.industry;
    } else if (activeTab === 'geography') {
      allocationData = portfolioData.currentAllocation.geography;
    } else if (activeTab === 'assetClass') {
      allocationData = portfolioData.currentAllocation.assetClass;
    }
    
    // Color mappings for segments
    const colorMap = {
      // Industry sectors
      informationTechnology: "#7C3AED", // purple-600
      consumerDiscretionary: "#9333EA", // purple-500
      financials: "#C084FC", // purple-400
      healthcare: "#3B82F6", // blue-500
      industrials: "#60A5FA", // blue-400
      communication: "#93C5FD", // blue-300
      consumerStaples: "#22C55E", // green-500
      energy: "#4ADE80", // green-400
      materials: "#86EFAC", // green-300
      utilities: "#9CA3AF", // gray-400
      realEstate: "#D1D5DB", // gray-300
      
      // Geography
      northAmerica: "#7C3AED", // purple-600 
      europe: "#9333EA", // purple-500
      asiaPacific: "#3B82F6", // blue-500
      emergingMarkets: "#22C55E", // green-500
      japan: "#9CA3AF", // gray-400
      
      // Asset classes
      domesticEquity: "#7C3AED", // purple-600
      internationalEquity: "#3B82F6", // blue-500
      fixedIncome: "#22C55E", // green-500
      alternatives: "#EAB308", // yellow-500
      cash: "#9CA3AF" // gray-400
    };
    
    // Convert allocation data to segments with cumulative percentages
    let cumulativePercentage = 0;
    segments = Object.keys(allocationData).map(key => {
      const percentage = allocationData[key];
      const startPercentage = cumulativePercentage;
      cumulativePercentage += percentage;
      return {
        key,
        percentage,
        startPercentage,
        endPercentage: cumulativePercentage,
        color: colorMap[key] || "#d1d5db" // Default to gray if color not found
      };
    });
    
    // Create the SVG donut chart
    return (
      <div className="relative">
        <svg className="w-full h-full" viewBox="0 0 100 100">
          {/* Segments */}
          {segments.map((segment, index) => {
            // Convert percentages to angles for SVG arcs
            const startAngle = (segment.startPercentage / 100) * 360;
            const endAngle = (segment.endPercentage / 100) * 360;
            
            // Calculate the SVG arc path
            const startRad = (startAngle - 90) * Math.PI / 180; // -90 to start at top
            const endRad = (endAngle - 90) * Math.PI / 180;
            
            const x1 = 50 + 40 * Math.cos(startRad);
            const y1 = 50 + 40 * Math.sin(startRad);
            const x2 = 50 + 40 * Math.cos(endRad);
            const y2 = 50 + 40 * Math.sin(endRad);
            
            // For arc path
            const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
            const pathData = `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
            
            return (
              <path
                key={index}
                d={pathData}
                fill={segment.color}
                stroke="white"
                strokeWidth="1"
              />
            );
          })}
          
          {/* Inner circle for donut hole */}
          <circle cx="50" cy="50" r="25" fill={isDarkMode ? '#1f2937' : 'white'} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center flex-col">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{portfolioData.value}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">Total</span>
        </div>
      </div>
    );
  };

  // Handle opening the edit form for a specific trade
  const handleEditTrade = (trade, index) => {
    setEditingTradeIndex(index);
    setEditForm({
      shares: trade.shares,
      price: trade.price.replace('$', ''),
      limitPrice: trade.limitPrice || '',
      orderType: trade.orderType || 'Market'
    });
  };
  
  // Handle saving the edited trade
  const handleSaveTradeEdit = () => {
    if (editingTradeIndex !== null) {
      // In a real app, this would update the backend data
      // For demo purposes, we'll just log it
      console.log(`Edited trade ${editingTradeIndex}:`, editForm);
      // Close the edit form
      setEditingTradeIndex(null);
    }
  };
  
  const handleExecute = () => {
    setExecutionStep('confirm');
  };
  
  const handleConfirm = () => {
    setIsExecuting(true);
    // Simulate API call
    setTimeout(() => {
      setExecutionStep('success');
      setIsExecuting(false);
    }, 1500);
  };
  
  const handleFinish = () => {
    // In a real app, you would update task status
    if (onExecuteTrades) {
      onExecuteTrades();
    }
    onClose();
  };
  
  // Render tab navigation for allocation views
  const renderAllocationTabs = () => {
    return (
      <div className="flex justify-center border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('industry')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'industry' 
              ? 'text-emerald-400 dark:text-emerald-300 border-b-2 border-emerald-400 dark:border-emerald-300' 
              : 'text-gray-500 dark:text-gray-200 hover:text-gray-700 dark:hover:text-white'
          }`}
        >
          Sector
        </button>
        <button
          onClick={() => setActiveTab('geography')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'geography' 
              ? 'text-emerald-400 dark:text-emerald-300 border-b-2 border-emerald-400 dark:border-emerald-300' 
              : 'text-gray-500 dark:text-gray-200 hover:text-gray-700 dark:hover:text-white'
          }`}
        >
          Geography
        </button>
        <button
          onClick={() => setActiveTab('assetClass')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'assetClass' 
              ? 'text-emerald-400 dark:text-emerald-300 border-b-2 border-emerald-400 dark:border-emerald-300' 
              : 'text-gray-500 dark:text-gray-200 hover:text-gray-700 dark:hover:text-white'
          }`}
        >
          Asset Class
        </button>
      </div>
    );
  };
  
  // Get color scheme based on sector/category (matching Risk Profile card)
  const getCategoryColor = (category, isIndustry = true) => {
    // Colors for industry sectors (matching RiskProfileCard colors)
    const industryColors = {
      informationTechnology: "bg-purple-700",
      consumerDiscretionary: "bg-purple-500",
      financials: "bg-purple-300",
      healthcare: "bg-blue-500",
      industrials: "bg-blue-400",
      communication: "bg-blue-300",
      consumerStaples: "bg-green-500",
      energy: "bg-green-400",
      materials: "bg-green-300",
      utilities: "bg-gray-400",
      realEstate: "bg-gray-300"
    };
    
    // Colors for geography
    const geographyColors = {
      northAmerica: "bg-purple-700",
      europe: "bg-purple-500",
      asiaPacific: "bg-blue-500",
      emergingMarkets: "bg-green-500",
      japan: "bg-gray-400"
    };
    
    // Colors for asset classes
    const assetClassColors = {
      domesticEquity: "bg-purple-700",
      internationalEquity: "bg-blue-500",
      fixedIncome: "bg-green-500",
      alternatives: "bg-yellow-500",
      cash: "bg-gray-400"
    };
    
    if (activeTab === 'industry') return industryColors[category] || "bg-gray-300";
    if (activeTab === 'geography') return geographyColors[category] || "bg-gray-300";
    return assetClassColors[category] || "bg-gray-300";
  };
  
  // Format category name for display
  const formatCategoryName = (category) => {
    return category
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
  };
  
  // Render the color-coded allocation section
  const renderAllocationSummary = () => {
    // Select which allocation data to display based on active tab
    let allocationData = [];
    
    if (activeTab === 'industry') {
      allocationData = Object.keys(portfolioData.currentAllocation.industry).map(key => ({
        name: formatCategoryName(key),
        color: getCategoryColor(key, true),
        current: portfolioData.currentAllocation.industry[key],
        target: portfolioData.targetAllocation.industry[key]
      }));
    } else if (activeTab === 'geography') {
      allocationData = Object.keys(portfolioData.currentAllocation.geography).map(key => ({
        name: formatCategoryName(key),
        color: getCategoryColor(key, false),
        current: portfolioData.currentAllocation.geography[key],
        target: portfolioData.targetAllocation.geography[key]
      }));
    } else if (activeTab === 'assetClass') {
      allocationData = Object.keys(portfolioData.currentAllocation.assetClass).map(key => ({
        name: formatCategoryName(key),
        color: getCategoryColor(key, false),
        current: portfolioData.currentAllocation.assetClass[key],
        target: portfolioData.targetAllocation.assetClass[key]
      }));
    }
    
    return (
      <div className="mt-4">
        {renderAllocationTabs()}
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Allocation Changes</h4>
        <div className="space-y-2">
          {allocationData.map((category) => (
            <div key={category.name} className="flex items-center">
              <div className={`w-4 h-4 ${category.color} rounded-sm mr-2`}></div>
              <span className="text-xs text-gray-700 dark:text-gray-300 w-32">{category.name}</span>
              <div className="flex-grow relative h-5 bg-gray-100 dark:bg-gray-700 rounded-sm overflow-hidden">
                <div className="flex w-full h-full">
                  <div className={`${category.color} h-full transition-all duration-500`} style={{ width: `${category.current}%` }}></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-between px-2">
                  <span className="text-xs font-medium text-white">{category.current}%</span>
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-200">→ {category.target}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Render the edit form for a trade
  const renderEditForm = (trade) => {
    return (
      <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
        <div className="mb-2 flex justify-between items-center">
          <span className="font-medium text-blue-800 dark:text-blue-300">{trade.action} {trade.symbol}</span>
          <button onClick={() => setEditingTradeIndex(null)} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Shares</label>
            <input 
              type="number" 
              value={editForm.shares} 
              onChange={(e) => setEditForm({...editForm, shares: parseInt(e.target.value) || 0})}
              className="w-full p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Price ($)</label>
            <input 
              type="text" 
              value={editForm.price} 
              onChange={(e) => setEditForm({...editForm, price: e.target.value})}
              className="w-full p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
        
        <div className="mb-2">
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Order Type</label>
          <select
            value={editForm.orderType}
            onChange={(e) => setEditForm({...editForm, orderType: e.target.value})}
            className="w-full p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="Market">Market</option>
            <option value="Limit">Limit</option>
          </select>
        </div>
        
        {editForm.orderType === 'Limit' && (
          <div className="mb-2">
            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Limit Price ($)</label>
            <input 
              type="text" 
              value={editForm.limitPrice} 
              onChange={(e) => setEditForm({...editForm, limitPrice: e.target.value})}
              className="w-full p-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
        )}
        
        <button
          onClick={handleSaveTradeEdit}
          className="w-full mt-1 px-3 py-1 bg-blue-600 dark:bg-blue-700 text-white text-sm rounded hover:bg-blue-700 dark:hover:bg-blue-600"
        >
          Save Changes
        </button>
      </div>
    );
  };
  
  // Render the suggested trades table
  const renderSuggestedTrades = () => {
    // Calculate totals for display
    const totalSells = portfolioData.suggestedTrades
      .filter(trade => trade.action === "Sell")
      .reduce((sum, trade) => sum + parseFloat(trade.amount.replace('$', '').replace(',', '')), 0);
      
    const totalBuys = portfolioData.suggestedTrades
      .filter(trade => trade.action === "Buy")
      .reduce((sum, trade) => sum + parseFloat(trade.amount.replace('$', '').replace(',', '')), 0);
    
    return (
      <div className="mt-6">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Suggested Trades</h4>
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">Action</th>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">Symbol</th>
                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">Shares</th>
                <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">Price</th>
                <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">Amount</th>
                <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">Edit</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {portfolioData.suggestedTrades.map((trade, index) => (
                <React.Fragment key={index}>
                  <tr className={trade.action === 'Buy' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        trade.action === 'Buy' 
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' 
                          : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300'
                      }`}>
                        {trade.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{trade.symbol}</td>
                    <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 max-w-xs">
                      <div className="truncate" title={trade.name}>
                        {trade.name}
                      </div>
                      {trade.orderType === 'Limit' && (
                        <div className="text-xs text-blue-600 dark:text-blue-400">Limit: {trade.limitPrice}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 text-right">{trade.shares}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 text-right">{trade.price}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 text-right">{trade.amount}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-center">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTrade(trade, index);
                        }}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  {editingTradeIndex === index && (
                    <tr>
                      <td colSpan="7" className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20">
                        {renderEditForm(trade)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              <tr className="bg-gray-50 dark:bg-gray-800">
                <td colSpan="5" className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 text-right">Total Sells:</td>
                <td className="px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 text-right">{formatCurrency(totalSells)}</td>
                <td></td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <td colSpan="5" className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 text-right">Total Buys:</td>
                <td className="px-3 py-2 text-sm font-medium text-green-600 dark:text-green-400 text-right">{formatCurrency(totalBuys)}</td>
                <td></td>
              </tr>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <td colSpan="5" className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 text-right">Net Cash Flow:</td>
                <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatCurrency(totalSells - totalBuys)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex justify-end mt-2">
          <button 
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center"
            onClick={() => console.log("Add new trade")}
          >
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Trade
          </button>
        </div>
      </div>
    );
  };
  
  // Render the portfolio summary with updated client info format
  const renderPortfolioSummary = () => {
    return (
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{portfolioData.clientName}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">{portfolioData.institution}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{portfolioData.accountNumber}</p>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{portfolioData.value}</div>
          <div className="flex items-center justify-end">
            <span className="text-green-600 dark:text-green-400 text-sm font-medium mr-1">↑ {portfolioData.performancePercent}%</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{portfolioData.dateRange}</span>
          </div>
        </div>
      </div>
    );
  };
  
  // Render the impact on risk profile with updated metrics format
  const renderRiskImpact = () => {
    // Calculate correct alignment percentages
    const riskInitialAlignment = Math.round((70 / 75) * 100); // 93% aligned
    const riskFinalAlignment = 100; // Will be 100% aligned after +5 points
    const healthScore = 87; // Financial health score as a percentage of 100
    
    // Get appropriate color gradients based on score
    const riskGradient = getScoreGradient(riskFinalAlignment); // 100% = emerald colors
    const healthGradient = getScoreGradient(healthScore); // 87% = green colors
    
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg mt-6">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Impact on Client Profiles</h4>
        
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700 dark:text-gray-300">Risk Profile</span>
              <div className="flex items-center">
                <span className={`font-medium ${riskGradient.text} dark:text-gray-200`}>70 <span className="text-lg mx-1">→</span> 75</span>
                <span className={`ml-2 text-xs px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 rounded-full`}>+5</span>
              </div>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden relative">
              {/* Target zone (full width) */}
              <div className="absolute top-0 bottom-0 left-0 bg-emerald-100 opacity-30" style={{ width: "100%" }}></div>
              
              {/* Current value (93%) */}
              <div 
                className={`absolute h-full ${riskGradient.progress} transition-all duration-500 ease-out`} 
                style={{ width: `${riskInitialAlignment}%` }}
              ></div>
              
              {/* Goal value indicator - remaining 7% with different opacity */}
              <div 
                className={`absolute h-full ${riskGradient.progress} opacity-50 transition-all duration-500 ease-out`} 
                style={{ width: "100%", clipPath: `inset(0 0 0 ${riskInitialAlignment}%)` }}
              ></div>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700 dark:text-gray-300">Financial Health Score</span>
              <div className="flex items-center">
                <span className={`font-medium ${healthGradient.text} dark:text-gray-200`}>85 <span className="text-lg mx-1">→</span> 87</span>
                <span className={`ml-2 text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 rounded-full`}>+2</span>
              </div>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden relative">
              {/* Target zone (full width) */}
              <div className="absolute top-0 bottom-0 left-0 bg-green-100 opacity-30" style={{ width: "100%" }}></div>
              
              {/* Current value (85%) */}
              <div 
                className={`absolute h-full ${healthGradient.progress} transition-all duration-500 ease-out`} 
                style={{ width: "85%" }}
              ></div>
              
              {/* Goal value indicator - additional 2% with different opacity */}
              <div 
                className={`absolute h-full ${healthGradient.progress} opacity-50 transition-all duration-500 ease-out`} 
                style={{ width: "87%", clipPath: `inset(0 0 0 85%)` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Calculate confirmation summary data
  const totalSellsAmount = portfolioData.suggestedTrades
    .filter(trade => trade.action === "Sell")
    .reduce((sum, trade) => sum + parseFloat(trade.amount.replace('$', '').replace(',', '')), 0);
    
  const totalBuysAmount = portfolioData.suggestedTrades
    .filter(trade => trade.action === "Buy")
    .reduce((sum, trade) => sum + parseFloat(trade.amount.replace('$', '').replace(',', '')), 0);
    
  if (!isOpen) return null;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
    >
      <div className="p-6">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-full mr-3">
                <svg className="w-6 h-6 text-blue-900 dark:text-blue-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Execute Trades</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {executionStep === 'review' ? 'Review and approve the proposed trades' : 
                   executionStep === 'confirm' ? 'Confirm your trade decisions' : 
                   'Trade execution successful'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Workflow steps */}
          <div className="mt-6 flex items-center justify-center">
            <div className="flex items-center w-4/5">
              <div className={`flex flex-col items-center ${executionStep === 'review' ? 'text-blue-100' : 'text-green-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  executionStep === 'review' 
                    ? 'bg-blue-900 text-blue-100' 
                    : 'bg-green-900 text-green-400'
                }`}>
                  {executionStep === 'review' ? '1' : '✓'}
                </div>
                <span className="text-xs mt-1 text-gray-100">Review</span>
              </div>
              <div className={`flex-grow h-0.5 mx-2 ${executionStep === 'review' ? 'bg-gray-700' : 'bg-green-600'}`}></div>
              <div className={`flex flex-col items-center ${
                executionStep === 'confirm' 
                  ? 'text-blue-100' 
                  : executionStep === 'success' 
                    ? 'text-green-400' 
                    : 'text-gray-600'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  executionStep === 'confirm' 
                    ? 'bg-blue-900 text-blue-100' 
                    : executionStep === 'success' 
                      ? 'bg-green-900 text-green-400' 
                      : 'bg-gray-800 text-gray-600'
                }`}>
                  {executionStep === 'success' ? '✓' : '2'}
                </div>
                <span className="text-xs mt-1 text-gray-100">Confirm</span>
              </div>
              <div className={`flex-grow h-0.5 mx-2 ${executionStep === 'success' ? 'bg-green-600' : 'bg-gray-700'}`}></div>
              <div className={`flex flex-col items-center ${
                executionStep === 'success' 
                  ? 'text-green-400' 
                  : 'text-gray-600'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  executionStep === 'success' 
                    ? 'bg-green-900 text-green-400' 
                    : 'bg-gray-800 text-gray-600'
                }`}>
                  {executionStep === 'success' ? '✓' : '3'}
                </div>
                <span className="text-xs mt-1 text-gray-100">Complete</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mt-6">
          {executionStep === 'review' && (
            <>
              {renderPortfolioSummary()}
              
              <div className="grid grid-cols-5 gap-6 mt-6">
                {/* Left column - allocation visualization */}
                <div className="col-span-2">
                  <div className="relative">
                    <div className="text-center mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Portfolio Allocation</span>
                    </div>
                    <div className="flex justify-center">
                      <div className="w-56 h-56 relative">
                        {renderDonutChart()}
                      </div>
                    </div>
                    
                    {renderAllocationSummary()}
                    {renderRiskImpact()}
                  </div>
                </div>
                
                {/* Right column - trades and metrics */}
                <div className="col-span-3">
                  {renderSuggestedTrades()}
                </div>
              </div>
              
              <div className="flex justify-end mt-8 space-x-4">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExecute}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
                >
                  Proceed to Execution
                </button>
              </div>
            </>
          )}
          
          {executionStep === 'confirm' && (
            <div className="max-w-2xl mx-auto text-center">
              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg mb-6 text-left">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="h-5 w-5 text-amber-500 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300">Confirmation Required</h3>
                    <div className="mt-2 text-sm text-amber-700 dark:text-amber-400">
                      <p>You are about to execute trades with a total value of {formatCurrency(totalSellsAmount + totalBuysAmount)} that will:</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        {portfolioData.suggestedTrades.filter(trade => trade.action === "Sell").map((trade, index) => (
                          <li key={`sell-${index}`}>Sell {trade.shares} shares of {trade.symbol} ({trade.amount})</li>
                        ))}
                        {portfolioData.suggestedTrades.filter(trade => trade.action === "Buy").map((trade, index) => (
                          <li key={`buy-${index}`}>Buy {trade.shares} shares of {trade.symbol} ({trade.amount})</li>
                        ))}
                      </ul>
                      <p className="mt-2">Total Sells: {formatCurrency(totalSellsAmount)} | Total Buys: {formatCurrency(totalBuysAmount)}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                Please confirm that you have reviewed these trades and wish to execute them. This action cannot be undone.
              </p>
              
              {isExecuting ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-900 dark:border-blue-400 mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Processing trades...</p>
                </div>
              ) : (
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => setExecutionStep('review')}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    Back to Review
                  </button>
                  <button
                    onClick={handleConfirm}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200"
                  >
                    Confirm and Execute
                  </button>
                </div>
              )}
            </div>
          )}
          
          {executionStep === 'success' && (
            <div className="max-w-2xl mx-auto text-center">
              <div className="mb-6">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900">
                  <svg className="h-10 w-10 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="mt-3 text-lg font-medium text-gray-900 dark:text-gray-100">Trades Executed Successfully!</h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  All trades have been placed and will be processed according to the market schedule.
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-6 text-left">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Trade Summary</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Total Sells</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{formatCurrency(totalSellsAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Total Buys</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{formatCurrency(totalBuysAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Net Cash Flow</p>
                    <p className="text-sm font-medium text-red-600 dark:text-red-400">{formatCurrency(totalSellsAmount - totalBuysAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Estimated Fees</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">$0.00</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg mb-8 text-left">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="h-5 w-5 text-green-500 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800 dark:text-green-300">Task Progress Updated</h3>
                    <div className="mt-2 text-sm text-green-700 dark:text-green-400">
                      <p>The task "Execute trades to increase Risk Profile by 5 points" has been marked as complete.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleFinish}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200"
              >
                Return to Task
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const TaskRecommendations = ({ 
  suggestions = [], 
  onSuggestionCheck, 
  onGenerateNew,
  loading = false
}) => {
  const { isDarkMode } = useTheme();
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showTradeExecution, setShowTradeExecution] = useState(false);
  
  // Track subtask completion status for each task
  const [taskSubtasks, setTaskSubtasks] = useState({});
  
  // Generate new suggestions function
  const handleGenerateNew = () => {
    if (onGenerateNew) {
      onGenerateNew();
    }
  };
  
  // Add the getTaskImpact function here to ensure it's available
  const getTaskImpact = (taskText) => {
    // Default values
    let riskImpact = 0;
    let healthImpact = 0;
    
    if (!taskText) return { riskImpact, healthImpact };
    
    // Set impact values based on task content
    const taskLower = taskText.toLowerCase();
    
    if (taskLower.includes('rebalance')) {
      if (taskLower.includes('reduce risk')) {
        riskImpact = -5; // Negative means reducing risk
      } else if (taskLower.includes('increase risk')) {
        riskImpact = 5;  // Positive means increasing risk
      } else {
        riskImpact = 2;  // Default rebalance impact
      }
      healthImpact = 3;
    } else if (taskLower.includes('rmd') || taskLower.includes('required minimum')) {
      riskImpact = 0;
      healthImpact = 5;
    } else if (taskLower.includes('tax loss')) {
      riskImpact = 0;
      healthImpact = 4;
    } else {
      // Default modest impacts for other tasks
      riskImpact = 1;
      healthImpact = 2;
    }
    
    return { riskImpact, healthImpact };
  };
  
  // Initialize subtask tracking on component mount or when suggestions change
  React.useEffect(() => {
    const subtaskStatus = {};
    suggestions.forEach((suggestion, index) => {
      const subtasks = generateSubtasks(suggestion.text);
      // Initialize all subtasks as not completed by default
      subtaskStatus[index] = Array(subtasks.length).fill(false);
      
      // For the second task (index 1), mark the first subtask as completed
      // This will show "In Progress" and have "Send meeting invitation to client" crossed off
      if (index === 1) {
        subtaskStatus[index][0] = true;
      }
    });
    setTaskSubtasks(subtaskStatus);
  }, [suggestions]);
  
  // Open task detail modal
  const handleViewDetails = (task) => {
    // Add subtask completion information to the task
    const taskWithSubtasks = {
      ...task,
      subtaskCompletion: taskSubtasks[task.id] || [],
      subtasks: generateSubtasks(task.text).map((text, index) => ({
        text,
        isComplete: taskSubtasks[task.id]?.[index] || false
      }))
    };
    setSelectedTask(taskWithSubtasks);
    setShowDetail(true);
  };
  
  // Handle complete task action
  const handleComplete = (taskId) => {
    if (onSuggestionCheck) {
      const taskIndex = suggestions.findIndex(task => task.id === taskId);
      if (taskIndex !== -1) {
        onSuggestionCheck(taskIndex);
      }
    }
    setShowDetail(false);
  };
  
  // Handle opening the trade execution modal
  const handleOpenTradeExecution = () => {
    setShowDetail(false); // Hide detail modal
    setShowTradeExecution(true); // Show trade execution modal
  };
  
  // Handle completing trades
  const handleCompleteTradeExecution = () => {
    if (selectedTask && onSuggestionCheck) {
      const taskIndex = suggestions.findIndex(task => task.id === selectedTask.id);
      if (taskIndex !== -1) {
        onSuggestionCheck(taskIndex);
      }
    }
    setShowTradeExecution(false);
  };
  
  // Update subtask completion from modal
  const handleSubtaskUpdate = (taskId, completionStatus) => {
    if (taskId !== undefined) {
      const newTaskSubtasks = {...taskSubtasks};
      newTaskSubtasks[taskId] = completionStatus;
      setTaskSubtasks(newTaskSubtasks);
    }
  };
  
  // Placeholder functions for note and reminder actions
  const handleAddNote = (taskId, note) => {
    console.log(`Added note to task ${taskId}: ${note}`);
    // Implement actual note saving logic here
  };
  
  const handleSetReminder = (taskId, date) => {
    console.log(`Set reminder for task ${taskId}: ${date}`);
    // Implement actual reminder setting logic here
  };
  
  const generateNewButton = (
    <button 
      onClick={handleGenerateNew}
      disabled={loading}
      className={`
        inline-flex items-center justify-center
        px-4 py-2 text-xs font-medium
        ${isDarkMode 
          ? 'bg-[linear-gradient(121deg,rgba(8,29,40,0.8)_0%,rgba(16,58,68,0.8)_35%,rgba(22,76,90,0.8)_100%)] text-white'
          : 'bg-blue-600 text-white hover:bg-blue-700'
        }
        rounded-md
        transition-all duration-150
        ${loading ? 'opacity-80 cursor-not-allowed' : ''}
      `}
    >
      {loading ? (
        <div className="flex items-center">
          <span className="animate-pulse bg-white h-1.5 w-1.5 rounded-full mr-1"></span>
          <span>Fetching insights...</span>
        </div>
      ) : (
        <>
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          Generate New Suggestions
      </>
    )}
  </button>
);

return (
  <>
    <DashboardCard
      title="Intelligent Task Recommendations"
      subtitle="Personalized suggestions based on client profile and market conditions"
      icon={ClipboardDocumentListIcon}
      headerControls={generateNewButton}
    >
      {loading ? (
        <div className="flex justify-center items-center p-4">
          <div className="flex flex-col items-center">
            <div className="flex space-x-2">
              <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse delay-200"></div>
              <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse delay-400"></div>
            </div>
            <p className="text-[10px] text-gray-400 mt-3">Fetching fresh insights...</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          <div className="grid grid-cols-3 gap-4 px-4 pt-1 pb-0">
            {suggestions.map((suggestion, index) => {
              const priorityColors = getPriorityStyle(suggestion.tag);
              const subtasks = generateSubtasks(suggestion.text);
              const subtaskCompletion = taskSubtasks[index] || Array(subtasks.length).fill(false);
              const title = index === 1 ? "Schedule Annual Review" : extractTitle(suggestion.text);
              
              return (
                <div 
                  key={index}
                  onClick={() => handleViewDetails({...suggestion, id: index, _forcedTitle: title})}
                  className="relative overflow-hidden rounded-lg transition-all duration-200 
                            bg-white dark:bg-black/80 border border-gray-200 dark:border-white/10
                            cursor-pointer hover:bg-gray-50 dark:hover:bg-black/70
                            h-fit group"
                >
                  {/* Color-coded left border */}
                  <div className={`absolute top-0 left-0 w-0.5 h-full ${priorityColors.border}`}></div>

                  <div className="px-3 py-2 pb-8">
                    {/* Header: Title and Tags */}
                    <div className="flex justify-between items-start mb-1.5">
                      <h3 className="text-[10px] font-medium text-gray-900 dark:text-white leading-tight">{title}</h3>
                      <div className="flex space-x-1 ml-2 flex-nowrap overflow-x-auto">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium flex-shrink-0 ${priorityColors.bg} ${priorityColors.text}`}>
                          {suggestion.tag.text}
                        </span>
                        {getTaskTags(subtaskCompletion).map((tag, idx) => (
                          <span key={idx} className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium flex-shrink-0 ${tag.bg} ${tag.textColor}`}>
                            {tag.text}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Subtasks */}
                    <ul className="list-disc list-inside text-[9px] text-gray-600 dark:text-gray-300 space-y-0.5 leading-snug pl-1">
                      {subtasks.map((task, idx) => (
                        <li 
                          key={idx}
                          className={subtaskCompletion[idx] ? 'line-through text-gray-500' : ''}
                        >
                          {task}
                        </li>
                      ))}
                    </ul>

                    {/* View Details link */}
                    <div className="absolute bottom-2 right-4">
                      <span className={`text-[9px] ${isDarkMode ? 'text-teal-300' : 'text-blue-600'} hover:underline group-hover:underline`}>View Details</span>
                    </div>

                    {/* Progress Bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-100 dark:bg-gray-800">
                      <div 
                        className={`h-full ${priorityColors.border} transition-all duration-500 ease-out`}
                        style={{ 
                          width: `${(subtaskCompletion.filter(st => st).length / subtaskCompletion.length) * 100}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Footer with View All Tasks link */}
          <div className="mt-2 py-1 pb-1 px-3 flex justify-end">
            <RouterLink
              to="/advisor-portal/tasks"
              className="text-xs text-blue-600 dark:text-teal-300 hover:underline"
            >
              Review All Tasks
            </RouterLink>
          </div>
        </div>
      )}
    </DashboardCard>
    
    {/* Task Detail Modal */}
    <TaskDetailModal 
      isOpen={showDetail}
      onClose={() => setShowDetail(false)}
      task={selectedTask}
      onComplete={handleComplete}
      onAddNote={handleAddNote}
      onSetReminder={handleSetReminder}
      onSubtaskUpdate={handleSubtaskUpdate}
      onOpenTradeExecution={handleOpenTradeExecution}
    />
    
    {/* Trade Execution Modal */}
    <TradeExecutionModal 
      isOpen={showTradeExecution}
      onClose={() => setShowTradeExecution(false)}
      task={selectedTask}
      taskImpact={selectedTask ? getTaskImpact(selectedTask.text) : { riskImpact: 0, healthImpact: 0 }}
      onExecuteTrades={handleCompleteTradeExecution}
    />
  </>
);
}

export default TaskRecommendations; 